import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Target, Zap, CheckCircle2 } from 'lucide-react';
import { SectionTitle, InsightBox, MR, JB } from './DemoShared';

export function Actions({ club, dt }) {
  const a = dt.actions;
  return (
    <section data-testid="section-actions">
      <SectionTitle
        label={a.eyebrow}
        title={a.title}
        subtitle={a.subtitle(club.name)}
      />
      <div className="space-y-5 mb-8">
        {club.actions.map((act, i) => (
          <div key={i} className="rounded-xl border border-border/40 bg-card/40 p-6 hover:border-primary/20 transition-colors" data-testid={`action-${i}`}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/15 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-primary" style={MR}>{i + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-[15px] font-semibold text-foreground mb-2" style={MR}>{act.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">{act.desc}</p>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(150,50%,45%)]/[0.08] border border-[hsl(150,50%,45%)]/15">
                  <Target className="w-3.5 h-3.5 text-[hsl(150,50%,45%)]" />
                  <span className="text-xs font-semibold text-[hsl(150,50%,45%)]">{act.impact}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <InsightBox>
        {a.disclaimer(club.name)}
      </InsightBox>
    </section>
  );
}

export function FinalCTA({ club, dt, onExplorePilot }) {
  const f = dt.finalCta;
  const { revenue } = club;

  return (
    <section className="py-8 md:py-12" data-testid="section-cta">
      <div className="max-w-2xl mx-auto">
        <div className="rounded-xl border border-primary/20 bg-primary/[0.04] p-6 mb-10" data-testid="opportunity-recap">
          <p className="text-xs text-primary/60 uppercase tracking-wider mb-4" style={JB}>{f.summaryLabel}</p>
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-2xl font-bold text-foreground" style={MR}>{club.brandScore}<span className="text-sm text-muted-foreground">/100</span></p>
              <p className="text-[11px] text-muted-foreground">{f.brandScoreLabel}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground" style={MR}>&euro;{club.brandValue}M</p>
              <p className="text-[11px] text-muted-foreground">{f.brandValueLabel}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-[hsl(150,50%,45%)]" style={MR}>&euro;{revenue.total.low}&ndash;{revenue.total.high}M</p>
              <p className="text-[11px] text-muted-foreground">{f.opportunityLabel}</p>
            </div>
          </div>
          <p className="text-sm text-foreground/80 leading-relaxed">
            {f.summaryText(club.name)}
          </p>
        </div>

        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-4" style={MR}>
            {f.headline}<br />{f.headlineBreak}
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-lg mx-auto">
            {f.subtitle(club.name)}
          </p>
        </div>

        <div className="rounded-xl border border-border/30 bg-card/40 p-5 mb-6 text-left">
          <p className="text-xs text-muted-foreground/60 uppercase tracking-wider mb-3" style={JB}>{f.deliversLabel}</p>
          <ul className="space-y-2.5">
            {f.deliverables.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/80">
                <CheckCircle2 className="w-4 h-4 text-[hsl(150,50%,45%)] flex-shrink-0 mt-0.5" /> {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-start gap-3 p-4 rounded-xl border border-primary/15 bg-primary/[0.03] mb-10" data-testid="urgency-card">
          <Zap className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-sm text-foreground/80 leading-relaxed">
            {f.urgency}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center" data-testid="final-demo-ctas">
          <Button size="lg" onClick={onExplorePilot} className="bg-primary hover:bg-primary/90 font-semibold h-13 px-8 text-[15px] rounded-lg" data-testid="demo-cta-briefing">
            {f.ctaMain} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Link to="/">
            <Button variant="outline" size="lg" className="h-13 px-6 text-[15px] rounded-lg border-border/60 text-muted-foreground hover:text-foreground" data-testid="demo-cta-home">
              {f.backToOverview}
            </Button>
          </Link>
        </div>

        <p className="text-[11px] text-muted-foreground/40 mt-8 max-w-md mx-auto leading-relaxed text-center">
          {f.disclaimerText}
        </p>
      </div>
    </section>
  );
}
